/**
 * Direction A identity artwork for Cluvik Admin — dimensional soft 3D with the
 * selective Cluvik ribbon.
 *
 * Authority: DS_DIRECTION_A_APPROVED.md in sk-amroar/cluvik-platform.
 *
 * TWO RULES THIS FILE EXISTS TO KEEP
 *
 * 1. SILHOUETTE CARRIES THE IDENTITY, colour never does. Every form below is
 *    drawn so that at 100% grey the eight are still distinguishable. When two
 *    read alike the fix is the shape — do not reach for contrast, and do not
 *    reach for a new hue.
 *
 * 2. THE RIBBON IS SELECTIVE. A partial arc drawn BEHIND the form, never a ring
 *    around it, and deliberately absent where the silhouette already carries
 *    framing (governance's shield, commerce's tag) — it is a brand signature,
 *    not a badge, and a badge on everything signifies nothing.
 *
 * Light and dark are ONE artwork. The bed reads from CSS custom properties, so
 * a theme switch does not render a different mark and no filter recolours one.
 * A filter would hit the coloured form too, which is how a dark-mode pass turns
 * a gradient into a grey blob.
 *
 * NOT FOR ACTIONS. Filters, edit, delete, sort, pagination and every other
 * control stay flat and monochrome — lucide, currentColor, one stroke weight.
 * If you are reaching for this component to decorate a button, the answer is no.
 */
import React, { useId } from 'react';
import { RAMP, identityPx, depthFor } from '../tokens/identityTokens';
import { identityForPath } from './adminIdentity';

const VB = 64;

function Defs({ u, lo, mid, hi, depth }) {
  return (
    <defs>
      <linearGradient id={`f${u}`} x1="0.14" y1="0" x2="0.76" y2="1">
        <stop offset="0%" stopColor={lo} />
        <stop offset="54%" stopColor={mid} />
        <stop offset="100%" stopColor={hi} />
      </linearGradient>
      <linearGradient id={`s${u}`} x1="0" y1="0" x2="1" y2="0.65">
        <stop offset="0%" stopColor={hi} />
        <stop offset="100%" stopColor={mid} />
      </linearGradient>
      <linearGradient id={`t${u}`} x1="0" y1="0" x2="0.28" y2="1">
        <stop offset="0%" stopColor="#fff" stopOpacity="0.88" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0.06" />
      </linearGradient>
      <linearGradient id={`rb${u}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={lo} stopOpacity="0.95" />
        <stop offset="100%" stopColor="#00C8D8" stopOpacity="0.85" />
      </linearGradient>
      <linearGradient id={`bed${u}`} x1="0.2" y1="0" x2="0.85" y2="1">
        <stop offset="0%" stopColor="var(--ds-identity-bed-a, #F5F8FF)" />
        <stop offset="100%" stopColor="var(--ds-identity-bed-b, #E1E9FA)" />
      </linearGradient>
      {depth.castShadow && (
        <filter id={`dr${u}`} x="-50%" y="-40%" width="200%" height="200%">
          <feDropShadow dx="0" dy="7" stdDeviation="6.5" floodColor={hi} floodOpacity="0.38" />
        </filter>
      )}
    </defs>
  );
}

/** An extruded rounded slab: side face first, then body, then the lit top. */
function Slab({ u, x, y, w, h, r = 4, d = 6 }) {
  return (
    <g>
      <rect x={x + d * 0.5} y={y + d * 0.6} width={w} height={h} rx={r} fill={`url(#s${u})`} />
      <rect x={x} y={y} width={w} height={h} rx={r} fill={`url(#f${u})`} />
      <rect x={x} y={y} width={w} height={h * 0.42} rx={r} fill={`url(#t${u})`} opacity="0.5" />
    </g>
  );
}

/**
 * The geometry. Each archetype is a function of the gradient id so the forms
 * cannot share a gradient across two marks on one page — an id collision
 * silently paints the second mark in the first one's colours.
 */
const FORMS = {
  // Stacked plates seen slightly from above — the estate, layered.
  overview: (u) => (
    <>
      <Slab u={u} x={12} y={30} w={40} h={9} r={3} d={4} />
      <Slab u={u} x={14} y={20} w={36} h={9} r={3} d={4} />
      <Slab u={u} x={16} y={10} w={32} h={9} r={3} d={4} />
    </>
  ),
  // A stepped pair of towers — a tenant is a PLACE, and a place has a skyline.
  //
  // A single block is too close to `product`'s box once both are grey; the step
  // and the doorway at ground level are what separate them by shape rather than
  // by the window detail, which vanishes at identity-sm.
  tenant: (u) => (
    <>
      <Slab u={u} x={11} y={24} w={19} h={26} r={4} d={6} />
      <Slab u={u} x={32} y={12} w={21} h={38} r={4} d={6} />
      <rect x={38} y={38} width={9} height={12} rx={1.5} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.92" />
      <rect x={16} y={31} width={6} height={6} rx={1.2} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.62" />
      <rect x={38} y={21} width={9} height={6} rx={1.2} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.62" />
    </>
  ),
  // A boxed module: outer shell with an inset lid seam.
  product: (u) => (
    <>
      <Slab u={u} x={13} y={18} w={38} h={30} r={5} d={7} />
      <rect x={13} y={26} width={38} height={3} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.55" />
      <rect x={29} y={18} width={6} height={30} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.35" />
    </>
  ),
  // A price tag — an angled body with a punched eyelet. No ribbon; the tag's
  // own diagonal already carries the motion.
  commerce: (u) => (
    <g>
      <path d="M36 12 L52 28 L30 50 L14 34 Z" fill={`url(#s${u})`} transform="translate(2.5,3.5)" />
      <path d="M36 12 L52 28 L30 50 L14 34 Z" fill={`url(#f${u})`} />
      <path d="M36 12 L52 28 L30 50 Z" fill={`url(#t${u})`} opacity="0.35" />
      <circle cx={39} cy={23} r={4.2} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.95" />
    </g>
  ),
  // Two nodes on a DIAGONAL, joined by a bar — the connection is the link.
  //
  // The first draft put them side by side at the same height with the bar
  // between them, and at 100% grey it read as a pair of sunglasses: two dark
  // lenses, a bridge, and the ribbon arcing over as a brow. That is the exact
  // failure DS_DIRECTION_A_APPROVED.md records for Contact, whose two figures
  // merged into "one head wearing sunglasses". The fix there was geometry and
  // it is geometry here: bilateral symmetry at eye height is what makes a
  // viewer see a face, so the composition is offset and the nodes are
  // different sizes. Do not restore the symmetric version because it balances
  // better — balance is what caused it.
  connection: (u) => (
    <>
      <rect
        x={26} y={26} width={13} height={13} rx={2}
        fill={`url(#f${u})`}
        transform="rotate(45 32.5 32.5)"
      />
      <Slab u={u} x={10} y={12} w={20} h={18} r={6} d={5} />
      <Slab u={u} x={35} y={35} w={20} h={18} r={6} d={5} />
    </>
  ),
  // A release wedge: a body pointing up out of a base plate.
  platform: (u) => (
    <>
      <Slab u={u} x={14} y={42} w={36} h={8} r={3} d={4} />
      <g>
        <path d="M32 10 L44 30 L32 38 L20 30 Z" fill={`url(#s${u})`} transform="translate(2,3)" />
        <path d="M32 10 L44 30 L32 38 L20 30 Z" fill={`url(#f${u})`} />
        <path d="M32 10 L32 38 L20 30 Z" fill={`url(#t${u})`} opacity="0.4" />
      </g>
    </>
  ),
  // A shield. No ribbon — the outline is already a frame.
  governance: (u) => (
    <g>
      <path d="M32 10 L50 17 V33 C50 43 42 50 32 54 C22 50 14 43 14 33 V17 Z" fill={`url(#s${u})`} transform="translate(2,3)" />
      <path d="M32 10 L50 17 V33 C50 43 42 50 32 54 C22 50 14 43 14 33 V17 Z" fill={`url(#f${u})`} />
      <path d="M32 10 L50 17 V33 C50 43 42 50 32 54 Z" fill={`url(#t${u})`} opacity="0.28" />
    </g>
  ),
  // A figure whose shoulder carries a key notch — a person WITH access, which
  // is what an operator is; a plain figure would be the Users identity.
  operator: (u) => (
    <>
      <g>
        <circle cx={32} cy={20} r={9.5} fill={`url(#s${u})`} transform="translate(2,3)" />
        <circle cx={32} cy={20} r={9.5} fill={`url(#f${u})`} />
      </g>
      <g>
        <path d="M14 52 C14 40 22 34 32 34 C42 34 50 40 50 52 Z" fill={`url(#s${u})`} transform="translate(2,3)" />
        <path d="M14 52 C14 40 22 34 32 34 C42 34 50 40 50 52 Z" fill={`url(#f${u})`} />
      </g>
      <rect x={40} y={40} width={12} height={5} rx={2.5} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.9" />
      <rect x={48} y={40} width={3} height={9} rx={1.4} fill="var(--ds-identity-bed-a, #F5F8FF)" opacity="0.9" />
    </>
  ),
};

/** Where the ribbon is deliberately absent. */
const NO_RIBBON = new Set(['commerce', 'governance']);

/**
 * @param {string}  path      Admin destination path — resolves archetype + ramp.
 * @param {string}  archetype Overrides the resolved archetype.
 * @param {string}  ramp      Overrides the resolved ramp position.
 * @param {string|number} size  Token name (xs…2xl) or a pixel number.
 * @param {string}  label     Accessible name. Omit when a visible label sits
 *                            beside the mark — the artwork is then decorative
 *                            and announcing it twice is worse than silence.
 */
export default function IdentityMark({
  path, archetype, ramp, size = 'md', label, className,
}) {
  const u = useId().replace(/[^a-zA-Z0-9]/g, '');
  const resolved = identityForPath(path);
  const arch = archetype || resolved.archetype;
  const rampKey = ramp || resolved.ramp;
  const [lo, mid, hi] = RAMP[rampKey] || RAMP.blue;
  const px = identityPx(size);
  const depth = depthFor(px);
  const draw = FORMS[arch] || FORMS.overview;

  return (
    <svg
      width={px}
      height={px}
      viewBox={`0 0 ${VB} ${VB}`}
      className={className}
      role={label ? 'img' : 'presentation'}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      focusable="false"
      data-archetype={arch}
      data-ramp={rampKey}
    >
      <Defs u={u} lo={lo} mid={mid} hi={hi} depth={depth} />
      <rect x="0" y="0" width={VB} height={VB} rx={14} fill={`url(#bed${u})`} />
      {depth.ribbon && !NO_RIBBON.has(arch) && (
        <path
          d="M8 44 C8 22 22 8 44 8"
          fill="none"
          stroke={`url(#rb${u})`}
          strokeWidth={5}
          strokeLinecap="round"
        />
      )}
      <g filter={depth.castShadow ? `url(#dr${u})` : undefined}>{draw(u)}</g>
    </svg>
  );
}
